import React, { useState, useEffect, useCallback } from 'react';
import { Search, Download, Building2, Users, Filter, ChevronLeft, ChevronRight, Loader2, Mail, Phone, MapPin } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ENTITY_LABELS = {
  private_limited: 'Private Limited',
  llp: 'LLP',
  partnership: 'Partnership',
  proprietorship: 'Proprietorship',
  one_person_company: 'One Person Company',
  llc: 'LLC',
  section_8: 'Section 8',
};

const ContactsPanel = ({ token }) => {
  const [view, setView] = useState('companies');
  const [filters, setFilters] = useState(null);
  const [entityType, setEntityType] = useState('');
  const [state, setState] = useState('');
  const [productId, setProductId] = useState('');
  const [designation, setDesignation] = useState('');
  const [search, setSearch] = useState('');
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const pageSize = 50;
  const totalPages = Math.ceil(total / pageSize);

  // Load filter options on mount
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/contacts/filters`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const d = await res.json();
        setFilters(d);
      } catch { /* ignore */ }
    };
    loadFilters();
  }, [token]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(pageSize) });
      if (entityType) params.set('entity_type', entityType);
      if (state) params.set('state', state);
      if (search) params.set('search', search);
      if (view === 'companies' && productId) params.set('product_id', productId);
      if (view === 'directors' && designation) params.set('designation', designation);

      const endpoint = view === 'companies' ? 'companies' : 'directors';
      const res = await fetch(`${API_URL}/api/admin/contacts/${endpoint}?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await res.json();
      setData(d[endpoint] || []);
      setTotal(d.total || 0);
    } catch {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [view, entityType, state, productId, designation, search, page, token]);

  useEffect(() => { loadData(); }, [loadData]);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [view, entityType, state, productId, designation, search]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (entityType) params.set('entity_type', entityType);
      if (state) params.set('state', state);
      if (search) params.set('search', search);
      if (view === 'companies' && productId) params.set('product_id', productId);
      if (view === 'directors' && designation) params.set('designation', designation);

      const endpoint = view === 'companies' ? 'companies' : 'directors';
      const res = await fetch(`${API_URL}/api/admin/contacts/${endpoint}/download?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${endpoint}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${total} ${endpoint}`);
    } catch {
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="contacts-panel">
      {/* View Toggle */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setView('companies')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            view === 'companies' ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/40' : 'text-slate-500 hover:text-white hover:bg-slate-800 border border-transparent'
          }`}
          data-testid="view-companies-btn"
        >
          <Building2 className="w-3.5 h-3.5" /> Companies
        </button>
        <button
          onClick={() => setView('directors')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            view === 'directors' ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/40' : 'text-slate-500 hover:text-white hover:bg-slate-800 border border-transparent'
          }`}
          data-testid="view-directors-btn"
        >
          <Users className="w-3.5 h-3.5" /> Directors / Partners
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            placeholder={view === 'companies' ? 'Search company...' : 'Search director/partner...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-xs bg-slate-800 border-slate-700 text-white"
            data-testid="contacts-search"
          />
        </div>

        {filters && (
          <>
            <select
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              className="h-8 px-2 text-xs bg-slate-800 border border-slate-700 rounded-md text-white"
              data-testid="filter-entity-type"
            >
              <option value="">All Types</option>
              {filters.entity_types.map(et => (
                <option key={et} value={et}>{ENTITY_LABELS[et] || et.replace('_', ' ')}</option>
              ))}
            </select>

            <select
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="h-8 px-2 text-xs bg-slate-800 border border-slate-700 rounded-md text-white"
              data-testid="filter-state"
            >
              <option value="">All States</option>
              {filters.states.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            {view === 'companies' && (
              <select
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                className="h-8 px-2 text-xs bg-slate-800 border border-slate-700 rounded-md text-white"
                data-testid="filter-product"
              >
                <option value="">All Products</option>
                {filters.products.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            )}

            {view === 'directors' && (
              <select
                value={designation}
                onChange={(e) => setDesignation(e.target.value)}
                className="h-8 px-2 text-xs bg-slate-800 border border-slate-700 rounded-md text-white"
                data-testid="filter-designation"
              >
                <option value="">All Roles</option>
                {filters.designations.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            )}
          </>
        )}

        <Button
          size="sm"
          variant="outline"
          onClick={handleDownload}
          disabled={downloading || total === 0}
          className="h-8 px-3 text-xs border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
          data-testid="download-contacts-btn"
        >
          {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Download className="w-3.5 h-3.5 mr-1" />}
          Download CSV ({total})
        </Button>
      </div>

      {/* Results Table */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
        </div>
      ) : data.length === 0 ? (
        <div className="text-center py-8 text-slate-500 text-sm">No results found</div>
      ) : view === 'companies' ? (
        <div className="border border-slate-700/50 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-800/80">
                <th className="text-left py-2 px-3 text-xs text-slate-400 font-medium">Company</th>
                <th className="text-left py-2 px-3 text-xs text-slate-400 font-medium">Email</th>
                <th className="text-left py-2 px-3 text-xs text-slate-400 font-medium">Phone</th>
                <th className="text-left py-2 px-3 text-xs text-slate-400 font-medium">Type</th>
                <th className="text-left py-2 px-3 text-xs text-slate-400 font-medium">State</th>
              </tr>
            </thead>
            <tbody>
              {data.map((c, i) => (
                <tr key={c.id || i} className="border-t border-slate-800/50 hover:bg-slate-800/30">
                  <td className="py-2 px-3 text-white text-xs font-medium">{c.company_name}</td>
                  <td className="py-2 px-3">
                    {c.company_email ? (
                      <a href={`mailto:${c.company_email}`} className="text-indigo-400 hover:underline text-xs flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {c.company_email}
                      </a>
                    ) : <span className="text-slate-600 text-xs">-</span>}
                  </td>
                  <td className="py-2 px-3">
                    {c.company_phone ? (
                      <span className="text-slate-300 text-xs flex items-center gap-1"><Phone className="w-3 h-3" /> {c.company_phone}</span>
                    ) : <span className="text-slate-600 text-xs">-</span>}
                  </td>
                  <td className="py-2 px-3 text-slate-400 text-xs">{ENTITY_LABELS[c.entity_type] || c.entity_type}</td>
                  <td className="py-2 px-3 text-slate-400 text-xs">{c.state || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="border border-slate-700/50 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-800/80">
                <th className="text-left py-2 px-3 text-xs text-slate-400 font-medium">Name</th>
                <th className="text-left py-2 px-3 text-xs text-slate-400 font-medium">Email</th>
                <th className="text-left py-2 px-3 text-xs text-slate-400 font-medium">Phone</th>
                <th className="text-left py-2 px-3 text-xs text-slate-400 font-medium">Role</th>
                <th className="text-left py-2 px-3 text-xs text-slate-400 font-medium">Company</th>
              </tr>
            </thead>
            <tbody>
              {data.map((d, i) => (
                <tr key={d.id || i} className="border-t border-slate-800/50 hover:bg-slate-800/30">
                  <td className="py-2 px-3 text-white text-xs font-medium">{d.full_name}</td>
                  <td className="py-2 px-3">
                    {d.email ? (
                      <a href={`mailto:${d.email}`} className="text-indigo-400 hover:underline text-xs flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {d.email}
                      </a>
                    ) : <span className="text-slate-600 text-xs">-</span>}
                  </td>
                  <td className="py-2 px-3">
                    {d.phone ? (
                      <span className="text-slate-300 text-xs flex items-center gap-1"><Phone className="w-3 h-3" /> {d.phone}</span>
                    ) : <span className="text-slate-600 text-xs">-</span>}
                  </td>
                  <td className="py-2 px-3 text-xs">
                    <span className="px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-300 text-[10px]">{d.designation || '-'}</span>
                  </td>
                  <td className="py-2 px-3 text-slate-400 text-xs">{d.company_name || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-slate-500">
            Page {page} of {totalPages} ({total} results)
          </span>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
              className="h-7 px-2 text-[11px] border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-30"
              data-testid="contacts-prev-page">
              <ChevronLeft className="w-3 h-3 mr-1" /> Prev
            </Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
              className="h-7 px-2 text-[11px] border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-30"
              data-testid="contacts-next-page">
              Next <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsPanel;
